/**
 * @file search.ts
 * @description Core AddSearch functions: search and suggest.
 * Safe to call from both server components and API routes.
 * Reference: https://www.addsearch.com/docs/api-reference/search-api/
 */

import { addSearchClient } from './client';
import type { SearchParams, SearchResponse, SuggestParams, SuggestResponse } from './types';

/**
 * Resolves the AddSearch site key from environment variables.
 * Throws at call-time if the key is missing so the error is surfaced immediately.
 */
const getSiteKey = (): string => {
  const key = process.env.NEXT_PUBLIC_ADDSEARCH_SITE_KEY;
  if (!key) {
    throw new Error(
      'Missing env var: NEXT_PUBLIC_ADDSEARCH_SITE_KEY. Add it to your .env.local file.'
    );
  }
  return key;
};

/**
 * Builds the query params object for the AddSearch search endpoint,
 * matching the expected URL shape:
 *   ?term=<q>&fuzzy=auto&collectAnalytics=false&page=1&limit=10&sort=relevance&order=desc&filter={}
 */
const buildSearchParams = (params: SearchParams): Record<string, string> => {
  const {
    term,
    fuzzy = 'auto',
    collectAnalytics = false,
    page = 1,
    limit = 10,
    sort = 'relevance',
    order = 'desc',
    filter = {},
    lang,
    documentType,
    categories,
    facet,
  } = params;

  const query: Record<string, string> = {
    term,
    fuzzy: String(fuzzy),
    collectAnalytics: String(collectAnalytics),
    page: String(page),
    limit: String(limit),
    sort,
    order,
    filter: JSON.stringify(filter),
  };

  if (lang) query.lang = lang;
  if (documentType) query.documentType = documentType;
  if (categories?.length) query.categories = categories.join(',');
  if (facet?.length) query.facet = facet.join(',');

  return query;
};

/**
 * Performs a full-text search against the AddSearch index.
 *
 * @example
 * const results = await search({ term: 'solar panels', page: 1, limit: 10 });
 */
export const search = async (params: SearchParams): Promise<SearchResponse> => {
  const siteKey = getSiteKey();
  const queryParams = buildSearchParams(params);

  const { data } = await addSearchClient.get<SearchResponse>(`/search/${siteKey}`, {
    params: queryParams,
  });

  return data;
};

/**
 * Fetches autocomplete keyword suggestions from AddSearch.
 *
 * @example
 * const { suggestions } = await suggest({ term: 'sol' });
 */
export const suggest = async (params: SuggestParams): Promise<SuggestResponse> => {
  const siteKey = getSiteKey();

  const queryParams: Record<string, string> = { term: params.term };
  if (params.lang) queryParams.lang = params.lang;
  if (params.size !== undefined) queryParams.size = String(params.size);

  const { data } = await addSearchClient.get<SuggestResponse>(`/suggest/${siteKey}`, {
    params: queryParams,
  });

  return data;
};
